import React, { useCallback, useState } from 'react'
import { useRouter } from 'expo-router'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { useThrottledCallback } from 'use-debounce'
import { useGameContext } from './GameContext'
import { useGameState } from './useGameState'
import { useSpectatorStatus } from './useSpectatorStatus'
import { GameOptionsDrawer, useGameOptionsDrawerStore } from './GameOptionsDrawer'
import { LeaveGameModal } from './Modals/LeaveGameModal'
import { flagVoluntaryLeave } from './GameModals'
import { spectatorReturnToLobby } from '../tournament/mttNavigation'

export const GameContentWithDrawer = ({ children }: { children: React.ReactNode }) => {
  const { game, isHost, isTeacherSeated } = useGameState()
  const { gameId, userId } = useGameContext()
  const { isSpectator } = useSpectatorStatus(gameId, userId)
  const router = useRouter()
  const endGameMutation = useMutation(api.games.mutations.endGame)
  const leaveGameMutation = useMutation(api.games.mutations.leaveGame)
  const leaveAsTeacherMutation = useMutation(api.games.teacherSeat.leaveAsTeacher)
  const unspectateGameMutation = useMutation(api.games.mutations.unspectateGame)
  const pauseGameMutation = useMutation(api.games.mutations.pauseGame)
  const [showLeaveGameModal, setShowLeaveGameModal] = useState(false)
  const playerStats = useQuery(
    api.playerStats.queries.getPlayerGameStats,
    gameId && userId ? { gameId, userId } : 'skip'
  )

  const isPractice = game?.mode === 'practice'
  const isTournament = game?.mode === 'mtt'

  const closeOptionsDrawer = useThrottledCallback(
    () => {
      useGameOptionsDrawerStore.getState().closeDrawer()
    },
    250,
    {
      leading: true
    }
  )

  // Leave game - removes self, game continues for others. Teacher uses leaveAsTeacher (no player record).
  const onPressLeave = useCallback(async () => {
    if (!(game && userId)) {
      return
    }
    setShowLeaveGameModal(false)
    const loungeId = game.loungeId
    flagVoluntaryLeave(game._id)
    try {
      if (isSpectator) {
        await unspectateGameMutation({ gameId: game._id, userId })
      } else if (isTeacherSeated) {
        await leaveAsTeacherMutation({ gameId: game._id, userId })
      } else {
        await leaveGameMutation({ gameId: game._id, userId })
      }
    } catch (error) {
      // TODO:  user was not in the game issue for observer
      console.error('Error leaving game:', error)
    }

    const isTerminal = game.state === 'finished' || game.state === 'deleted'
    if (isTournament && game.tournamentId) {
      // Leaving the game in a tournament context returns to the lobby
      // (which was pushed earlier in this stack). The user can then
      // close the tournament explicitly from the lobby header.
      spectatorReturnToLobby(router, game.tournamentId)
    } else if (!isTerminal && loungeId) {
      router.dismissTo({ pathname: '/(root)/lounges/[loungeId]', params: { loungeId } } as any)
    } else {
      router.dismissTo('/(root)/(tabs)/play')
    }
  }, [game, userId, isSpectator, isTeacherSeated, isTournament, leaveGameMutation, leaveAsTeacherMutation, unspectateGameMutation, router])

  // End Game - ends game for all (host only)
  const onPressFinish = useCallback(async () => {
    if (!game) {
      return
    }
    setShowLeaveGameModal(false)
    const loungeId = game.loungeId
    await endGameMutation({ gameId: game._id })
    if (loungeId) {
      router.dismissTo({ pathname: '/(root)/lounges/[loungeId]', params: { loungeId } } as any)
    } else {
      router.dismissTo('/(root)/(tabs)/play')
    }
  }, [game, endGameMutation, router])

  const onPressCancel = useCallback(() => {
    setShowLeaveGameModal(false)
    requestAnimationFrame(() => {
      setTimeout(() => {
        closeOptionsDrawer()
      }, 500)
    })
  }, [closeOptionsDrawer])

  const openLeaveModal = useCallback(async () => {
    closeOptionsDrawer()
    // Daily10: pause and navigate away (no leave modal needed for solo game)
    if (game?.mode === 'daily10') {
      if (game.state === 'playing') {
        await pauseGameMutation({ gameId: game._id })
      }
      router.dismissTo('/(root)/(tabs)/play')
      return
    }
    if (isTournament) {
      await onPressLeave()
      return
    }
    setShowLeaveGameModal(true)
  }, [closeOptionsDrawer, onPressLeave, isTournament, game?.mode, game?._id, pauseGameMutation, router])

  return (
    <>
      <GameOptionsDrawer onLeaveGame={openLeaveModal} gameCode={game?.code} gameName={game?.name}>
        {children}
      </GameOptionsDrawer>
      <LeaveGameModal
        visible={showLeaveGameModal}
        onCancel={onPressCancel}
        onLeave={onPressLeave}
        onFinish={onPressFinish}
        isHost={isHost && !isTournament}
        isPractice={isPractice}
        playerStats={playerStats}
      />
    </>
  )
}
